export interface ConsentPreferences {
  analytics: boolean;
  adsMeasurement: boolean;
}

export interface ConsentSnapshot {
  hasDecision: boolean;
  preferences: ConsentPreferences;
  policyVersion: string;
  savedAt?: string;
  expiresAt?: string;
}

export type GoogleConsentSignal = 'granted' | 'denied';

export interface GoogleConsentSignals {
  analytics_storage: GoogleConsentSignal;
  ad_storage: GoogleConsentSignal;
  ad_user_data: GoogleConsentSignal;
  ad_personalization: 'denied';
}

export type ConsentChangeListener = (
  snapshot: ConsentSnapshot,
  previousSnapshot: ConsentSnapshot,
) => void;

export type ConsentCategory = keyof ConsentPreferences;
export type ConsentWithdrawalCleanup = (snapshot: ConsentSnapshot) => void;

export interface ConsentChangeEventDetail {
  snapshot: ConsentSnapshot;
  previousSnapshot: ConsentSnapshot;
}

interface StoredConsentRecord {
  schemaVersion: number;
  policyVersion: string;
  preferences: ConsentPreferences;
  savedAt: string;
  expiresAt: string;
}

/**
 * Increment this whenever the consent choices or their explanation materially change. Records
 * from a different policy version are deliberately treated as undecided.
 */
export const CONSENT_POLICY_VERSION = '1';
export const CONSENT_STORAGE_KEY = 'saf:consent:v1';
export const CONSENT_CHANGE_EVENT_NAME = 'saf:consent-change';
export const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

const CONSENT_SCHEMA_VERSION = 1;
const FUTURE_CLOCK_SKEW_MS = 5 * 60 * 1000;
const MAX_TIMEOUT_MS = 2_147_483_647;

const listeners = new Set<ConsentChangeListener>();
const withdrawalCleanups: Record<ConsentCategory, Set<ConsentWithdrawalCleanup>> = {
  analytics: new Set<ConsentWithdrawalCleanup>(),
  adsMeasurement: new Set<ConsentWithdrawalCleanup>(),
};

let storageListenerInstalled = false;
let persistenceFailed = false;
let expiryTimer: number | null = null;
let scheduledExpirySnapshot: ConsentSnapshot | null = null;

function undecidedSnapshot(): ConsentSnapshot {
  return {
    hasDecision: false,
    preferences: {
      analytics: false,
      adsMeasurement: false,
    },
    policyVersion: CONSENT_POLICY_VERSION,
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isConsentPreferences(value: unknown): value is ConsentPreferences {
  return (
    isObject(value) &&
    typeof value.analytics === 'boolean' &&
    typeof value.adsMeasurement === 'boolean'
  );
}

function parseStoredConsent(raw: string | null, now = Date.now()): ConsentSnapshot | null {
  if (!raw) {
    return null;
  }

  try {
    const value = JSON.parse(raw) as unknown;
    if (
      !isObject(value) ||
      value.schemaVersion !== CONSENT_SCHEMA_VERSION ||
      value.policyVersion !== CONSENT_POLICY_VERSION ||
      !isConsentPreferences(value.preferences) ||
      typeof value.savedAt !== 'string' ||
      typeof value.expiresAt !== 'string'
    ) {
      return null;
    }

    const savedAtMs = Date.parse(value.savedAt);
    const expiresAtMs = Date.parse(value.expiresAt);
    if (
      !Number.isFinite(savedAtMs) ||
      !Number.isFinite(expiresAtMs) ||
      savedAtMs > now + FUTURE_CLOCK_SKEW_MS ||
      expiresAtMs <= now ||
      expiresAtMs - savedAtMs !== CONSENT_MAX_AGE_MS
    ) {
      return null;
    }

    return {
      hasDecision: true,
      preferences: {
        analytics: value.preferences.analytics,
        adsMeasurement: value.preferences.adsMeasurement,
      },
      policyVersion: value.policyVersion,
      savedAt: value.savedAt,
      expiresAt: value.expiresAt,
    };
  } catch {
    return null;
  }
}

function toStoredRecord(snapshot: ConsentSnapshot): StoredConsentRecord | null {
  if (!snapshot.hasDecision || !snapshot.savedAt || !snapshot.expiresAt) {
    return null;
  }

  return {
    schemaVersion: CONSENT_SCHEMA_VERSION,
    policyVersion: snapshot.policyVersion,
    preferences: {
      analytics: snapshot.preferences.analytics,
      adsMeasurement: snapshot.preferences.adsMeasurement,
    },
    savedAt: snapshot.savedAt,
    expiresAt: snapshot.expiresAt,
  };
}

function removeStoredConsent(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Storage can be unavailable. The caller still receives the denied, undecided snapshot.
  }
}

function clearExpiryTimer(): void {
  if (expiryTimer !== null) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  scheduledExpirySnapshot = null;
}

function scheduleConsentExpiry(snapshot: ConsentSnapshot): void {
  if (typeof window === 'undefined' || !snapshot.hasDecision || !snapshot.expiresAt) {
    clearExpiryTimer();
    return;
  }

  if (
    expiryTimer !== null &&
    scheduledExpirySnapshot?.expiresAt === snapshot.expiresAt &&
    snapshotsEqual(scheduledExpirySnapshot, snapshot)
  ) {
    return;
  }

  clearExpiryTimer();
  scheduledExpirySnapshot = snapshot;
  const expiresAtMs = Date.parse(snapshot.expiresAt);
  const delay = Math.max(0, Math.min(expiresAtMs - Date.now(), MAX_TIMEOUT_MS));
  expiryTimer = window.setTimeout(() => {
    expiryTimer = null;
    const previousSnapshot = scheduledExpirySnapshot ?? snapshot;
    scheduledExpirySnapshot = null;

    if (Date.now() < expiresAtMs) {
      // Browser timeouts are capped at roughly 24.8 days; schedule the remaining interval.
      scheduleConsentExpiry(previousSnapshot);
      return;
    }

    removeStoredConsent();
    const expiredSnapshot = undecidedSnapshot();
    dispatchConsentChange(expiredSnapshot, previousSnapshot);
  }, delay);
}

function snapshotsEqual(left: ConsentSnapshot, right: ConsentSnapshot): boolean {
  return (
    left.hasDecision === right.hasDecision &&
    left.policyVersion === right.policyVersion &&
    left.savedAt === right.savedAt &&
    left.expiresAt === right.expiresAt &&
    left.preferences.analytics === right.preferences.analytics &&
    left.preferences.adsMeasurement === right.preferences.adsMeasurement
  );
}

function runWithdrawalCleanups(
  snapshot: ConsentSnapshot,
  previousSnapshot: ConsentSnapshot,
): void {
  (['analytics', 'adsMeasurement'] as const).forEach((category) => {
    const isDenied = !snapshot.preferences[category];
    const wasGranted = previousSnapshot.preferences[category];
    const isFirstExplicitDenial = snapshot.hasDecision && !previousSnapshot.hasDecision;

    if (!isDenied || (!wasGranted && !isFirstExplicitDenial)) {
      return;
    }

    withdrawalCleanups[category].forEach((cleanup) => {
      try {
        cleanup(snapshot);
      } catch {
        // Cleanup failures must not prevent other privacy cleanup or consent notifications.
      }
    });
  });
}

function dispatchConsentChange(
  snapshot: ConsentSnapshot,
  previousSnapshot: ConsentSnapshot,
): void {
  if (snapshotsEqual(snapshot, previousSnapshot)) {
    return;
  }

  runWithdrawalCleanups(snapshot, previousSnapshot);

  listeners.forEach((listener) => {
    try {
      listener(snapshot, previousSnapshot);
    } catch {
      // One consumer must not prevent the remaining consumers from receiving the change.
    }
  });

  if (typeof window !== 'undefined' && typeof window.CustomEvent === 'function') {
    const detail: ConsentChangeEventDetail = { snapshot, previousSnapshot };
    window.dispatchEvent(
      new window.CustomEvent<ConsentChangeEventDetail>(CONSENT_CHANGE_EVENT_NAME, { detail }),
    );
  }
}

function snapshotFromStorageEventValue(raw: string | null): ConsentSnapshot {
  return parseStoredConsent(raw) ?? undecidedSnapshot();
}

function handleStorageChange(event: StorageEvent): void {
  let isLocalStorageEvent = false;
  try {
    // sessionStorage uses the same event shape. Never accept a similarly named record from an
    // iframe/session context as evidence that optional consent was persisted for this site.
    isLocalStorageEvent = event.storageArea === null || event.storageArea === window.localStorage;
  } catch {
    return;
  }
  const isConsentRecordChange = event.key === CONSENT_STORAGE_KEY;
  const isLocalStorageClear = event.key === null;
  if (!isLocalStorageEvent || (!isConsentRecordChange && !isLocalStorageClear)) {
    return;
  }

  // A browser storage event is authoritative evidence that persistence is available again.
  persistenceFailed = false;
  if (isLocalStorageClear) {
    const previousSnapshot = scheduledExpirySnapshot ?? undecidedSnapshot();
    clearExpiryTimer();
    dispatchConsentChange(undecidedSnapshot(), previousSnapshot);
    return;
  }

  const previousSnapshot = snapshotFromStorageEventValue(event.oldValue);
  const snapshot = snapshotFromStorageEventValue(event.newValue);
  scheduleConsentExpiry(snapshot);
  dispatchConsentChange(snapshot, previousSnapshot);
}

function ensureStorageListener(): void {
  if (storageListenerInstalled || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('storage', handleStorageChange);
  storageListenerInstalled = true;
}

/**
 * Necessary storage is implicit and cannot be disabled through this API. Optional consent fails
 * closed: only a current, validated, first-party record can grant analytics or ads measurement.
 */
export function getConsentSnapshot(): ConsentSnapshot {
  if (typeof window === 'undefined' || persistenceFailed) {
    clearExpiryTimer();
    return undecidedSnapshot();
  }

  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      const previousSnapshot = scheduledExpirySnapshot;
      clearExpiryTimer();
      const snapshot = undecidedSnapshot();
      if (previousSnapshot?.hasDecision) {
        dispatchConsentChange(snapshot, previousSnapshot);
      }
      return snapshot;
    }

    const snapshot = parseStoredConsent(raw);
    if (!snapshot) {
      const previousSnapshot = scheduledExpirySnapshot;
      removeStoredConsent();
      clearExpiryTimer();
      const deniedSnapshot = undecidedSnapshot();
      if (previousSnapshot?.hasDecision) {
        dispatchConsentChange(deniedSnapshot, previousSnapshot);
      }
      return deniedSnapshot;
    }

    scheduleConsentExpiry(snapshot);
    return snapshot;
  } catch {
    persistenceFailed = true;
    clearExpiryTimer();
    return undecidedSnapshot();
  }
}

export function saveConsentPreferences(preferences: ConsentPreferences): ConsentSnapshot {
  const previousSnapshot = getConsentSnapshot();
  if (!isConsentPreferences(preferences) || typeof window === 'undefined') {
    removeStoredConsent();
    const snapshot = undecidedSnapshot();
    dispatchConsentChange(snapshot, previousSnapshot);
    return snapshot;
  }

  const savedAtMs = Date.now();
  const snapshot: ConsentSnapshot = {
    hasDecision: true,
    preferences: {
      analytics: preferences.analytics,
      adsMeasurement: preferences.adsMeasurement,
    },
    policyVersion: CONSENT_POLICY_VERSION,
    savedAt: new Date(savedAtMs).toISOString(),
    expiresAt: new Date(savedAtMs + CONSENT_MAX_AGE_MS).toISOString(),
  };
  const storedRecord = toStoredRecord(snapshot);

  try {
    if (!storedRecord) {
      throw new Error('Consent record is incomplete.');
    }

    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(storedRecord));

    // Read the record back before granting anything. This keeps storage failures fail closed.
    const verifiedSnapshot = parseStoredConsent(
      window.localStorage.getItem(CONSENT_STORAGE_KEY),
      savedAtMs,
    );
    if (!verifiedSnapshot || !snapshotsEqual(verifiedSnapshot, snapshot)) {
      throw new Error('Consent record could not be verified.');
    }

    persistenceFailed = false;
    scheduleConsentExpiry(verifiedSnapshot);
    dispatchConsentChange(verifiedSnapshot, previousSnapshot);
    return verifiedSnapshot;
  } catch {
    // Keep all future reads denied until a consent write is successfully verified or another tab
    // produces an authoritative storage event. This prevents a stale grant from resurfacing after
    // a write/remove failure.
    persistenceFailed = true;
    removeStoredConsent();
    clearExpiryTimer();
    const deniedSnapshot = undecidedSnapshot();
    dispatchConsentChange(deniedSnapshot, previousSnapshot);
    return deniedSnapshot;
  }
}

export function acceptAllConsent(): ConsentSnapshot {
  return saveConsentPreferences({ analytics: true, adsMeasurement: true });
}

export function rejectNonEssentialConsent(): ConsentSnapshot {
  return saveConsentPreferences({ analytics: false, adsMeasurement: false });
}

export function hasAnalyticsConsent(): boolean {
  const snapshot = getConsentSnapshot();
  return snapshot.hasDecision && snapshot.preferences.analytics;
}

export function hasAdsMeasurementConsent(): boolean {
  const snapshot = getConsentSnapshot();
  return snapshot.hasDecision && snapshot.preferences.adsMeasurement;
}

/**
 * Maps the first-party decision to Google Consent Mode's four relevant signals. This service does
 * not load or call any external script. Callers choose whether to use Basic or Advanced Consent
 * Mode, while these signals always remain fail-closed until a corresponding grant exists.
 */
export function getGoogleConsentSignals(): GoogleConsentSignals {
  const snapshot = getConsentSnapshot();
  const analyticsGranted = snapshot.hasDecision && snapshot.preferences.analytics;
  const adsMeasurementGranted = snapshot.hasDecision && snapshot.preferences.adsMeasurement;

  return {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: adsMeasurementGranted ? 'granted' : 'denied',
    ad_user_data: adsMeasurementGranted ? 'granted' : 'denied',
    ad_personalization: 'denied',
  };
}

export function subscribeToConsentChanges(listener: ConsentChangeListener): () => void {
  listeners.add(listener);
  ensureStorageListener();

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Registers category-specific cleanup for consent withdrawal (for example, deleting analytics
 * cookies or queued ad attribution). Hooks also run on the first explicit denial so legacy state
 * can be removed. Cleanup errors are isolated from other hooks and listeners.
 */
export function registerConsentWithdrawalCleanup(
  category: ConsentCategory,
  cleanup: ConsentWithdrawalCleanup,
): () => void {
  withdrawalCleanups[category].add(cleanup);
  ensureStorageListener();

  return () => {
    withdrawalCleanups[category].delete(cleanup);
  };
}
